/**
 * ParlayCard and LegRow - Settled parlay display components
 */
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type { ReactElement } from 'react';
import type { SettledLegDetail, SettledParlayDetail } from '../../hooks/useLabDashboard';

// Void reason display configuration
const VOID_REASON_CONFIG: Record<
  string,
  { color: string; bg: string; label: string; displayText: string }
> = {
  dnp: {
    color: 'text-theme-text-secondary',
    bg: 'bg-theme-text-secondary',
    label: 'DNP',
    displayText: 'Did Not Play',
  },
  player_not_found: {
    color: 'text-orange-400',
    bg: 'bg-orange-500',
    label: '?',
    displayText: 'Player Not Found',
  },
  wrong_team: {
    color: 'text-purple-400',
    bg: 'bg-purple-500',
    label: '!',
    displayText: 'Wrong Team',
  },
  game_postponed: {
    color: 'text-theme-warning',
    bg: 'bg-theme-warning',
    label: 'PPD',
    displayText: 'Game Postponed',
  },
};

function LegRow({ leg }: { leg: SettledLegDetail }): ReactElement {
  // Determine if this is a game prop (spread, total, moneyline)
  const isGameProp =
    leg.statType &&
    ['spread', 'spreads', 'total', 'totals', 'moneyline', 'h2h'].includes(
      leg.statType.toLowerCase(),
    );

  // Determine leg status
  const getStatusInfo = () => {
    if (leg.dnp) {
      // Use specific void reason if available, fallback to generic DNP
      const voidConfig = leg.voidReason
        ? VOID_REASON_CONFIG[leg.voidReason]
        : VOID_REASON_CONFIG.dnp;
      return voidConfig || VOID_REASON_CONFIG.dnp;
    }
    if (leg.hit === true) {
      return {
        color: 'text-theme-success',
        bg: 'bg-theme-success',
        label: '✓',
        displayText: '',
      };
    }
    if (leg.hit === false) {
      return {
        color: 'text-theme-danger',
        bg: 'bg-theme-danger',
        label: '✗',
        displayText: '',
      };
    }
    return {
      color: 'text-theme-warning',
      bg: 'bg-theme-warning',
      label: '?',
      displayText: '',
    };
  };

  const status = getStatusInfo();

  // Format the prop display with stat type
  const statLabel = leg.statType ? formatStatLabel(leg.statType) : null;
  const propDisplay =
    leg.overUnder && leg.line
      ? `${leg.overUnder} ${leg.line}${statLabel ? ` ${statLabel}` : ''}`
      : leg.prop;

  // Format actual value (whole number)
  const actualDisplay =
    leg.actualValue !== null ? String(Math.round(parseFloat(leg.actualValue))) : '-';

  // Format game date/time
  const gameDateStr = leg.gameDate
    ? new Date(leg.gameDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      {/* Status indicator */}
      <div
        className={`w-6 h-6 rounded-full ${status.bg} flex items-center justify-center shrink-0`}
      >
        <span className="text-white text-xs font-bold">{status.label}</span>
      </div>

      {/* Player/Team and prop info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {/* For game props, show team vs opponent. For player props, show player name */}
          <span className="font-medium text-white truncate">
            {isGameProp
              ? leg.opponentTeam
                ? `${leg.team} vs ${leg.opponentTeam}`
                : leg.team
              : leg.player}
          </span>
          {/* For player props, show team in parentheses */}
          {!isGameProp && leg.team && (
            <span className="text-xs text-theme-text-tertiary shrink-0">({leg.team})</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-theme-text-secondary">{propDisplay}</span>
          {gameDateStr && <span className="text-gray-600 text-xs">{gameDateStr}</span>}
        </div>
      </div>

      {/* Actual value and odds */}
      <div className="text-right shrink-0">
        <div className={`text-sm font-medium ${status.color}`}>
          {leg.dnp ? status.displayText : `Actual: ${actualDisplay}`}
        </div>
        {leg.odds && !leg.dnp && <div className="text-xs text-theme-text-tertiary">{leg.odds}</div>}
      </div>
    </div>
  );
}

export function ParlayCard({ parlay }: { parlay: SettledParlayDetail }): ReactElement {
  const resultColors: Record<string, string> = {
    won: 'bg-theme-success/10 text-theme-success border-theme-success',
    lost: 'bg-theme-danger/10 text-theme-danger border-theme-danger',
    push: 'bg-theme-warning/10 text-theme-warning border-theme-warning',
    void: 'bg-theme-surface-hover text-theme-text border-theme-border',
    pending: 'bg-theme-primary/10 text-theme-primary border-theme-primary',
  };

  const resultClass = resultColors[parlay.result] || resultColors.pending;

  // Format tracked date/time
  const trackedStr = parlay.createdAt
    ? new Date(parlay.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  // Format tracked date
  const trackedDateStr = parlay.createdAt
    ? new Date(parlay.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <div className="bg-theme-surface rounded-lg border border-theme-border overflow-hidden">
      {/* Parlay Header */}
      <div className="p-4 border-b border-theme-border">
        {/* User Display Name, Email, and Tracked Date */}
        <div className="flex items-center justify-between mb-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">{parlay.userDisplayName || 'System'}</span>
            {parlay.userEmail && (
              <span className="text-theme-text-tertiary">({parlay.userEmail})</span>
            )}
          </div>
          {trackedStr && (
            <span className="text-xs text-theme-text-tertiary">Tracked {trackedStr}</span>
          )}
        </div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${resultClass}`}>
              {parlay.result.toUpperCase()}
            </span>
            {parlay.tier && (
              <span className="text-xs text-theme-text-tertiary bg-theme-surface-hover px-2 py-0.5 rounded">
                {parlay.tier}
              </span>
            )}
            {trackedDateStr && (
              <span className="text-xs text-theme-text-tertiary">{trackedDateStr}</span>
            )}
          </div>
          <div className="text-right">
            {parlay.finalOdds && parlay.finalOdds !== parlay.totalOdds ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-theme-text-tertiary line-through">
                  {parlay.totalOdds}
                </span>
                <span className="text-sm font-medium text-white">{parlay.finalOdds}</span>
              </div>
            ) : parlay.totalOdds ? (
              <span className="text-sm font-medium text-white">{parlay.totalOdds}</span>
            ) : null}
          </div>
        </div>

        {/* Stake and Payout */}
        <div className="flex items-center gap-4 text-sm">
          {parlay.stake && (
            <span className="text-theme-text-secondary">
              Stake: <span className="text-white">${parseFloat(parlay.stake).toFixed(2)}</span>
            </span>
          )}
          {parlay.result === 'won' && parlay.actualPayout && (
            <span className="text-theme-success">
              Won:{' '}
              <span className="font-medium">${parseFloat(parlay.actualPayout).toFixed(2)}</span>
            </span>
          )}
          {parlay.result === 'lost' && (
            <span className="text-theme-danger">
              Lost:{' '}
              <span className="font-medium">
                -${parlay.stake ? parseFloat(parlay.stake).toFixed(2) : '0.00'}
              </span>
            </span>
          )}
          {(parlay.result === 'push' || parlay.result === 'void') && parlay.actualPayout && (
            <span className="text-theme-text-secondary">
              Returned:{' '}
              <span className="text-white">${parseFloat(parlay.actualPayout).toFixed(2)}</span>
            </span>
          )}
        </div>
      </div>

      {/* Legs */}
      <div className="divide-y divide-gray-700/50">
        {parlay.legs.map((leg) => (
          <LegRow key={leg.id} leg={leg} />
        ))}
      </div>
    </div>
  );
}
